import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PHASES, ROLE_LABELS } from "../lib/methodology";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import WeeklySummaryPanel from "../components/WeeklySummaryPanel";

export default function DashboardPage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [projects, setProjects] = useState([]);
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/dashboard/stats"),
            api.get("/projects"),
            api.get("/dashboard/summary").catch(() => ({ data: null })),
        ])
            .then(([s, p, sm]) => { setStats(s.data); setProjects(p.data); setSummary(sm.data); })
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    return (
        <div className="space-y-10" data-testid="dashboard-page">
            <div>
                <div className="label-caps">Bem-vindo, {ROLE_LABELS[user?.role]}</div>
                <h1 className="font-display text-5xl text-[#0d1628] mt-4">Olá, {user?.name?.split(" ")[0]}.</h1>
                <p className="text-[#52525b] mt-4 max-w-2xl">
                    Aqui fica uma visão geral dos projetos de felicidade da sua organização.
                </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-grid">
                {[
                    { label: "Projetos", value: stats?.projects_total ?? "—", show: true },
                    { label: "Em curso", value: stats?.projects_active ?? "—", show: true },
                    { label: "Concluídos", value: stats?.projects_completed ?? "—", show: true },
                    {
                        label: "Utilizadores",
                        value: stats?.users_limit ? `${stats?.users_total ?? 0} / ${stats.users_limit}` : (stats?.users_total ?? "—"),
                        show: user?.role === "company_manager" || user?.role === "super_admin",
                    },
                ].filter((s) => s.show).map((s) => (
                    <div key={s.label} className="card-flat" data-testid={`stat-${s.label}`}>
                        <div className="label-caps">{s.label}</div>
                        <div className="font-display text-5xl text-[#0d1628] mt-6">{s.value}</div>
                    </div>
                ))}
            </div>

            <WeeklySummaryPanel summary={summary} />

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 card-flat">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="label-caps">Projetos recentes</div>
                            <h2 className="font-display text-3xl text-[#0d1628] mt-2">Em destaque</h2>
                        </div>
                        <Link to="/app/projects" className="btn-ghost" data-testid="see-all-projects">Ver todos</Link>
                    </div>
                    {projects.length === 0 ? (
                        <div className="text-[#52525b] text-sm mt-8">Ainda não há projetos. Comece por criar um em <Link to="/app/projects" className="link-underline">Projetos</Link>.</div>
                    ) : (
                        <ul className="mt-6 divide-y divide-[#e4e4e7]" data-testid="recent-projects">
                            {projects.slice(0, 5).map((p) => (
                                <li key={p.id} className="py-4 flex items-center justify-between">
                                    <div>
                                        <Link to={`/app/projects/${p.id}`} className="font-display text-2xl text-[#0d1628] hover:italic" data-testid={`recent-project-${p.id}`}>{p.name}</Link>
                                        <div className="label-caps mt-1">Fase atual: {PHASES[(p.current_phase || 1) - 1]?.title}</div>
                                    </div>
                                    <Link to={`/app/projects/${p.id}`} className="btn-ghost" data-testid={`open-project-${p.id}`}>
                                        Abrir <ArrowRight size={14} />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="card-flat">
                    <div className="label-caps">Distribuição por fase</div>
                    <h2 className="font-display text-3xl text-[#0d1628] mt-2">Onde estão os seus projetos</h2>
                    <ul className="mt-8 space-y-4">
                        {PHASES.map((ph) => {
                            const count = stats?.phase_distribution?.[String(ph.num)] ?? 0;
                            const total = stats?.projects_total || 1;
                            const pct = Math.min(100, Math.round((count / total) * 100));
                            return (
                                <li key={ph.num} data-testid={`phase-dist-${ph.num}`}>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-[#0d1628]">{ph.num}. {ph.title}</span>
                                        <span className="label-caps">{count}</span>
                                    </div>
                                    <div className="h-px bg-[#e4e4e7] mt-2 relative">
                                        <div className="absolute left-0 top-0 h-[2px] -mt-[1px] bg-[#0d1628]" style={{ width: `${pct}%` }} />
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}
